import { apolloCore } from "lib/apollo/apollo";
import { getResultQueryParams } from "lib/results/useQueryResult";
import { executeQueryAndAddToastOnError } from "shared/executeQuery/executeQueryAndAddToastOnError";
import { currentParametersForQuery } from "lib/parameters/hooks";
import type { ParameterMerge } from "lib/parameters/types";
import type { ActiveContext } from "shared/ContextSwitcher/store";
import type { Session } from "lib/users/types";
import type { SupportedPublicPerformanceTierKey } from "shared/SelectPerformance/useGetPerformanceTiers";
import { SupportedExecutionTypes } from "shared/executeQuery/callExecuteQueryV3";

export function executeQueries(args: {
  session: Session;
  activeContext: ActiveContext;
  performance: SupportedPublicPerformanceTierKey | undefined;
  queryIds: number[];
  parameters: ParameterMerge;
  apiKey: string | undefined;
}) {
  const {
    session,
    activeContext,
    queryIds,
    parameters,
    performance,
    apiKey,
  } = args;

  queryIds.forEach((queryId) => {
    const queryParameters = currentParametersForQuery(queryId, parameters);
    const usingDefaultParams = queryParameters.every(
      (p) => p.value === p.default_value
    );
    const getResultParams = getResultQueryParams({
      queryId,
      parameters: queryParameters,
      can_refresh: usingDefaultParams,
      apiKey: apiKey,
      session,
    });
    const executionType: SupportedExecutionTypes = usingDefaultParams
      ? "interactive"
      : "viz_custom_params_refresh";

    // Execute the query
    executeQueryAndAddToastOnError({
      queryId,
      parameters: getResultParams.variables.parameters,
      requestContext: getResultParams.context,
      activeContext,
      executionType,
      performance,
    })
      // Call the GetResult graphql query that is called as part of the useQueryResult
      // hook. Since we have just triggered a fresh execution, get a new result for this
      // graphql query should override the cache and trigger the polling workflow to
      // fetch the new result
      .then(() => {
        apolloCore.query({
          query: getResultParams.queryDoc,
          variables: getResultParams.variables,
          context: getResultParams.context,
          fetchPolicy: "network-only",
        });
      })
      .catch(() => {}); // Avoid unhandled promise rejection
  });
}
